// Persistência do agente GPH (favoritos, contadores Free e histórico limitado).
//
// Decisão de arquitetura (auditada contra firestore.rules):
// - Novas subcoleções são negadas por default nas rules atuais, então o store usa
//   exclusivamente coleções client-owned já permitidas:
//     users/{uid}/coach_state/gph_agent            → meta (favoritos, uso, conversas)
//     users/{uid}/coachChats/{convId}/messages/... → mensagens compactas
// - O validador isSafeClientCoachData proíbe chaves sensíveis (isPro, entitlements,
//   role, credits...). Este store NUNCA grava essas chaves — plano vira `plan`.
// - Nunca gravar vídeo, base64, frames ou payload bruto de análise: apenas texto
//   curto, capabilityId e referências.
//
// Limites de histórico (spec §13):
//   Free: 1 conversa ativa, 512 KB por conversa.
//   Pro : 2 MB por conversa, 50 MB por conta.

import { doc, getDoc, setDoc, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';

export const FREE_CONVERSATION_LIMIT_BYTES = 512 * 1024;
export const PRO_CONVERSATION_LIMIT_BYTES = 2 * 1024 * 1024;
export const PRO_ACCOUNT_LIMIT_BYTES = 50 * 1024 * 1024;

const META_COLLECTION = 'coach_state';
const META_DOC_ID = 'gph_agent';
const CHATS_COLLECTION = 'coachChats';

const SENSITIVE_KEYS = new Set([
  'credits', 'reservedCredits', 'total_spent', 'role', 'admin', 'isAdmin', 'isPro',
  'subscription', 'subscriptionStatus', 'stripeCustomerId', 'stripeSubscriptionId',
  'entitlements', 'purchases', 'calculatorLifetime', 'partnerActive', 'partnerCode',
  'referralCommissions'
]);

const stripSensitiveKeys = (data = {}) => {
  const safe = {};
  for (const [key, value] of Object.entries(data)) {
    if (!SENSITIVE_KEYS.has(key)) safe[key] = value;
  }
  return safe;
};

export const estimateBytes = (value) => {
  try {
    return new Blob([JSON.stringify(value ?? '')]).size;
  } catch {
    return String(value ?? '').length * 2;
  }
};

export const newConversationId = () =>
  `gph_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const createDefaultAgentState = () => ({
  favorites: [],
  usage: {},
  activeConversationId: null,
  conversationBytes: {},
  totalBytes: 0,
  // Contexto do Expert Core (últimos resultados + playerMemory permanente).
  // Nunca contém tokens, vídeo ou payloads ricos — só referências curtas.
  gphContext: null
});

export const loadAgentState = async (uid) => {
  if (!uid || !db) return createDefaultAgentState();
  try {
    const snapshot = await getDoc(doc(db, 'users', uid, META_COLLECTION, META_DOC_ID));
    if (!snapshot.exists()) return createDefaultAgentState();
    const data = snapshot.data() || {};
    return {
      ...createDefaultAgentState(),
      favorites: Array.isArray(data.favorites) ? data.favorites : [],
      usage: data.usage && typeof data.usage === 'object' ? data.usage : {},
      activeConversationId: data.activeConversationId || null,
      conversationBytes: data.conversationBytes && typeof data.conversationBytes === 'object' ? data.conversationBytes : {},
      totalBytes: Number.isFinite(Number(data.totalBytes)) ? Number(data.totalBytes) : 0,
      gphContext: data.gphContext && typeof data.gphContext === 'object' ? data.gphContext : null
    };
  } catch (error) {
    console.warn('[GPH-STORE] Falha ao carregar estado (seguindo em memória):', error?.message || error);
    return createDefaultAgentState();
  }
};

export const saveAgentState = async (uid, patch = {}) => {
  if (!uid || !db) return false;
  try {
    const safePatch = stripSensitiveKeys({ ...patch, updatedAt: new Date().toISOString() });
    await setDoc(doc(db, 'users', uid, META_COLLECTION, META_DOC_ID), safePatch, { merge: true });
    return true;
  } catch (error) {
    console.warn('[GPH-STORE] Falha ao salvar estado (seguindo em memória):', error?.message || error);
    return false;
  }
};

// Mensagem compacta persistida — apenas referências/resumos, nunca payloads ricos.
export const appendMessage = async (uid, conversationId, message = {}) => {
  if (!uid || !conversationId || !db) return 0;
  const record = stripSensitiveKeys({
    messageId: message.messageId || `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    conversationId,
    messageRole: message.role === 'user' ? 'user' : 'gph',
    type: message.type || 'text',
    text: String(message.text || '').slice(0, 2000),
    capabilityId: message.capabilityId || null,
    richBlockRef: message.richBlockRef || null,
    createdAt: new Date().toISOString()
  });
  record.estimatedBytes = estimateBytes(record);

  try {
    const messagesRef = collection(db, 'users', uid, CHATS_COLLECTION, conversationId, 'messages');
    await setDoc(doc(messagesRef, record.messageId), record);
  } catch (error) {
    console.warn('[GPH-STORE] Falha ao persistir mensagem (histórico local mantido):', error?.message || error);
  }
  return record.estimatedBytes;
};

export const loadConversationMessages = async (uid, conversationId, maxMessages = 80) => {
  if (!uid || !conversationId || !db) return [];
  try {
    const messagesRef = collection(db, 'users', uid, CHATS_COLLECTION, conversationId, 'messages');
    const snapshot = await getDocs(query(
      messagesRef,
      orderBy('createdAt', 'desc'),
      limit(maxMessages)
    ));
    const rows = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() || {};
      const role = data.role === 'user' || data.messageRole === 'user' ? 'user' : 'gph';
      const text = String(data.content || data.text || '').trim();
      if (!text) return;
      rows.push({
        id: data.messageId || docSnap.id,
        role,
        kind: data.type === 'notice' ? 'notice' : 'text',
        text,
        capabilityId: data.capabilityId || null,
        createdAt: data.createdAt || null
      });
    });
    return rows.reverse();
  } catch (error) {
    console.warn('[GPH-STORE] Falha ao carregar mensagens (seguindo com boas-vindas):', error?.message || error);
    return [];
  }
};

export const getConversationLimits = (plan = 'free') => (
  plan === 'pro'
    ? { perConversation: PRO_CONVERSATION_LIMIT_BYTES, perAccount: PRO_ACCOUNT_LIMIT_BYTES }
    : { perConversation: FREE_CONVERSATION_LIMIT_BYTES, perAccount: FREE_CONVERSATION_LIMIT_BYTES }
);

export const checkConversationLimit = ({ plan = 'free', conversationBytesUsed = 0, totalBytesUsed = 0 }) => {
  const limits = getConversationLimits(plan);
  if (conversationBytesUsed >= limits.perConversation) {
    return { blocked: true, scope: 'conversation', limits };
  }
  if (plan === 'pro' && totalBytesUsed >= limits.perAccount) {
    return { blocked: true, scope: 'account', limits };
  }
  return { blocked: false, scope: null, limits };
};

export default {
  loadAgentState,
  saveAgentState,
  appendMessage,
  loadConversationMessages,
  estimateBytes,
  newConversationId,
  checkConversationLimit,
  getConversationLimits
};
